/**
 * Chess Audio System
 * Provides sound effects for chess moves using MP3 files
 */

class ChessAudio {
  constructor() {
    this.enabled = true;
    this.volume = 0.3;
    this.sounds = {};
    this.userInteracted = false;
    this.pendingSounds = [];
    
    // Preload audio files
    this.loadSounds();
    
    // Set up user interaction detection
    this.setupUserInteractionDetection();
  }

  setupUserInteractionDetection() {
    const events = ['click', 'keydown', 'touchstart', 'mousedown'];
    
    const enableAudio = () => {
      this.userInteracted = true;
      
      // Try to initialize audio context and play any pending sounds
      this.initializeAudioContext();
      
      // Remove event listeners after first interaction
      events.forEach(event => {
        document.removeEventListener(event, enableAudio, { passive: true });
      });
    };

    // Add event listeners for user interaction
    events.forEach(event => {
      document.addEventListener(event, enableAudio, { passive: true });
    });
  }

  initializeAudioContext() {
    // Play a silent sound to initialize audio context
    Object.values(this.sounds).forEach(audio => {
      if (audio) {
        const currentVolume = audio.volume;
        audio.volume = 0;
        audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = currentVolume;
        }).catch(() => {
          // Ignore errors during initialization
        });
      }
    });

    // Play any sounds that were queued before user interaction
    this.playPendingSounds();
  }

  playPendingSounds() {
    while (this.pendingSounds.length > 0) {
      const soundKey = this.pendingSounds.shift();
      this.playSound(soundKey);
    }
  }

  loadSounds() {
    const soundFiles = {
      move: '/sounds/move.mp3',
      capture: '/sounds/capture.mp3',
      castle: '/sounds/castle.mp3',
      check: '/sounds/check.mp3',
      notify: '/sounds/notify.mp3',
      'game-start': '/sounds/game-start.mp3',
      'game-end': '/sounds/game-end.mp3',
      illegal: '/sounds/illegal.mp3'
    };

    Object.keys(soundFiles).forEach(key => {
      const audio = new Audio(soundFiles[key]);
      audio.preload = 'auto';
      audio.volume = this.volume;
      this.sounds[key] = audio;
    });
  }

  /**
   * Play an audio file
   * @param {string} soundKey - Key of the sound to play
   */
  playSound(soundKey) {
    if (!this.enabled || !this.sounds[soundKey]) return;

    // If user hasn't interacted yet, queue the sound for later
    if (!this.userInteracted) {
      this.pendingSounds.push(soundKey);
      return;
    }

    try {
      const audio = this.sounds[soundKey].cloneNode();
      audio.volume = this.volume;

      audio.play().catch(error => {
        // Only warn for actual playback errors, not autoplay policy errors
        if (error.name !== 'NotAllowedError') {
          console.warn(`Failed to play ${soundKey} sound:`, error);
        }
      });
    } catch (error) {
      console.warn(`Failed to play ${soundKey} sound:`, error);
    }
  }

  /**
   * Play normal move sound
   * Clean, pleasant wooden piece sound
   */
  playMoveSound() {
    this.playSound('move');
  }

  /**
   * Play capture move sound
   * More dramatic sound for captures
   */
  playCaptureSound() {
    this.playSound('capture');
  }

  /**
   * Play check sound
   * Attention-grabbing sound for check
   */
  playCheckSound() {
    this.playSound('check');
  }

  /**
   * Play castle sound
   * Special sound for castling moves
   */
  playCastleSound() {
    this.playSound('castle');
  }

  /**
   * Play notification sound
   * General notification/success sound
   */
  playNotifySound() {
    this.playSound('notify');
  }

  /**
   * Play completion sound
   * Celebratory sound for completing a line
   */
  playCompletionSound() {
    this.playNotifySound();
  }

  /**
   * Play game start sound
   * Welcome sound when starting a new game/line
   */
  playGameStartSound() {
    this.playSound('game-start');
  }

  /**
   * Play game end sound
   * Completion sound when finishing a game/line
   */
  playGameEndSound() {
    this.playSound('game-end');
  }

  /**
   * Play illegal move sound
   * Sound for invalid chess moves
   */
  playIllegalSound() {
    this.playSound('illegal');
  }

  /**
   * Play error sound
   * Subtle sound for wrong moves (uses illegal sound now)
   */
  playErrorSound() {
    this.playIllegalSound();
  }

  /**
   * Enable/disable sound
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Set volume
   * @param {number} volume - Volume (0-1)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    // Update volume for all loaded sounds
    Object.values(this.sounds).forEach(audio => {
      audio.volume = this.volume;
    });
  }

  /**
   * Check if audio is ready to play
   */
  isReady() {
    return this.userInteracted;
  }

  /**
   * Get current settings
   */
  getSettings() {
    return {
      enabled: this.enabled,
      volume: this.volume,
      soundsLoaded: Object.keys(this.sounds).length,
      userInteracted: this.userInteracted,
      pendingSounds: this.pendingSounds.length
    };
  }
}

// Create and export singleton instance
const chessAudio = new ChessAudio();

export default chessAudio;